import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, UserCheck, LogIn, LogOut } from 'lucide-react';

const VISITOR_TYPES = ['FAMILY', 'CONTRACTOR', 'THERAPIST', 'NDIS_REP', 'STAFF_VISITOR', 'OTHER'];

export default function VisitorLogsPage() {
  const [visitors, setVisitors] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [houses, setHouses] = useState([]);
  const [showCheckin, setShowCheckin] = useState(false);
  const [form, setForm] = useState({ property_id: '', visitor_name: '', visitor_type: 'FAMILY', purpose: '', notes: '' });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/accommodation/visitors?page=${page}&pageSize=20`);
      setVisitors(data.items || []); setTotal(data.total || 0);
    } catch { toast.error('Failed to load visitors'); }
  }, [page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/houses?pageSize=100').then(r => setHouses(r.data.items || r.data.data || [])).catch(() => {}); }, []);

  const handleCheckin = async () => {
    if (!form.property_id || !form.visitor_name) { toast.error('Property and name required'); return; }
    try {
      await api.post('/accommodation/visitors', form);
      toast.success('Visitor checked in');
      setShowCheckin(false); setForm({ property_id: '', visitor_name: '', visitor_type: 'FAMILY', purpose: '', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleCheckout = async (id) => {
    try {
      await api.post(`/accommodation/visitors/${id}/checkout`);
      toast.success('Visitor checked out');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="visitor-logs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><UserCheck className="w-6 h-6" />Visitor Logs</h1>
          <p className="text-muted-foreground mt-1">{total} visitor records</p>
        </div>
        <Button onClick={() => setShowCheckin(true)} data-testid="visitor-checkin-btn"><Plus className="w-4 h-4 mr-2" />Check In Visitor</Button>
      </div>

      {visitors.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No visitor records</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {visitors.map(v => (
            <Card key={v.id} data-testid={`visitor-${v.id}`}>
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">{(v.visitor_name || '?')[0]}</div>
                  <div>
                    <p className="text-sm font-medium">{v.visitor_name}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant="outline" className="text-[10px]">{v.visitor_type}</Badge>
                      <span>{v.property_name || v.property_id}</span>
                      {v.purpose && <span>· {v.purpose}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <div className="text-right">
                    <p className="flex items-center gap-1"><LogIn className="w-3 h-3 text-green-600" />{v.check_in?.slice(0, 16).replace('T', ' ')}</p>
                    {v.check_out ? <p className="flex items-center gap-1"><LogOut className="w-3 h-3 text-red-500" />{v.check_out?.slice(0, 16).replace('T', ' ')}</p> : null}
                  </div>
                  {!v.check_out && (
                    <Button size="sm" variant="outline" onClick={() => handleCheckout(v.id)} data-testid={`checkout-${v.id}`}>
                      <LogOut className="w-3 h-3 mr-1" />Check Out
                    </Button>
                  )}
                  {v.check_out && <Badge variant="outline">Checked Out</Badge>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {total > 20 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-sm text-muted-foreground">Page {page}</span>
          <Button variant="outline" size="sm" disabled={visitors.length < 20} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      <Dialog open={showCheckin} onOpenChange={setShowCheckin}>
        <DialogContent>
          <DialogHeader><DialogTitle>Visitor Check-In</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Property *</Label>
              <Select value={form.property_id} onValueChange={v => setForm(p => ({ ...p, property_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>{houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Visitor Name *</Label><Input value={form.visitor_name} onChange={e => setForm(p => ({ ...p, visitor_name: e.target.value }))} /></div>
            <div><Label>Visitor Type</Label>
              <Select value={form.visitor_type} onValueChange={v => setForm(p => ({ ...p, visitor_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{VISITOR_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Purpose</Label><Input value={form.purpose} onChange={e => setForm(p => ({ ...p, purpose: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckin(false)}>Cancel</Button>
            <Button onClick={handleCheckin} data-testid="confirm-checkin">Check In</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
